import React from "react";
import ReactECharts from "echarts-for-react";

export default function RevenueLine({ data, range, onRangeChange }) {
  const primaryColor = "#ff7a00";

  // ---------------------------------------
  // 🔹 Extract values
  // ---------------------------------------
  const allValues = data.series.flatMap((s) =>
    s.data.map((v) => Number(v || 0))
  );

  const rawMax = Math.max(...allValues, 0);
  const rawMin = Math.min(...allValues.filter((v) => v > 0));

  // Padding
  const paddedMax = rawMax * 1.15;
  const paddedMin = rawMin * 0.85;

  const minY =
    Number.isFinite(paddedMin) && paddedMin > 0
      ? Math.floor(paddedMin / 1000) * 1000
      : 0;

  // ---------------------------------------
  // 🔹 Dynamic splitNumber calculation
  // ---------------------------------------
  const CHART_HEIGHT = 320;
  const IDEAL_GRID_PX = 55; // 👈 visual sweet spot

  const dynamicSplitNumber = Math.max(
    4,
    Math.min(8, Math.round(CHART_HEIGHT / IDEAL_GRID_PX))
  );

  // ---------------------------------------
  // 🔹 Calculate clean interval
  // ---------------------------------------
  const roughInterval = (paddedMax - minY) / dynamicSplitNumber;

  const snapIntervals = [1000, 5000, 10000, 25000, 50000, 100000];

  const interval =
    snapIntervals.find((i) => i >= roughInterval) ||
    Math.ceil(roughInterval / 1000) * 1000;

  // Align maxY to interval → equal spacing
  const maxY = minY + interval * dynamicSplitNumber;

  // ---------------------------------------
  // 🔹 Chart configuration
  // ---------------------------------------
  const option = {
    backgroundColor: "#ffffff",

    grid: {
      left: 64,
      right: 32,
      top: 72,
      bottom: 48,
    },

    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "line",
        lineStyle: {
          color: "#d1d5db",
          width: 1,
          type: "dashed",
        },
      },
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderWidth: 1,
      padding: [10, 14],
      textStyle: {
        color: "#111827",
        fontSize: 12,
      },
      formatter: (params) => {
        const p = params[0];
        return `
          <div style="font-weight:600;margin-bottom:4px">
            ${p.name}
          </div>
          <div style="color:${p.color}">
            ${p.seriesName}: ₹${Number(p.value).toLocaleString()}
          </div>
        `;
      },
    },

    legend: {
      top: 16,
      right: 20,
      icon: "circle",
      itemWidth: 10,
      textStyle: {
        color: "#374151",
        fontSize: 12,
      },
    },

    xAxis: {
      type: "category",
      data: data.categories,
      boundaryGap: false,
      axisTick: { show: false },
      axisLine: {
        lineStyle: { color: "#e5e7eb" },
      },
      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
      },
    },

    yAxis: {
      type: "value",
      min: minY,
      max: maxY,
      splitNumber: dynamicSplitNumber, // 🔥 dynamic
      axisLine: { show: false },
      axisTick: { show: false },

      splitLine: {
        lineStyle: {
          color: "rgba(0,0,0,0.06)",
          type: "dashed",
        },
      },

      axisLabel: {
        color: "#6b7280",
        fontSize: 11,
        formatter: (v) => {
          if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
          if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
          return v;
        },
      },
    },

    series: data.series.map((s) => ({
      name: s.name,
      type: "line",
      smooth: true,
      data: s.data,
      showSymbol: false,

      lineStyle: {
        width: 3,
        color: primaryColor,
      },

      itemStyle: {
        color: primaryColor,
      },

      areaStyle: {
        color: "rgba(255,122,0,0.08)",
      },

      emphasis: {
        focus: "series",
      },

      // markPoint: {
      //   symbol: "circle",
      //   symbolSize: 10,
      //   data: [{ type: "max" }],
      //   label: {
      //     formatter: ({ value }) => `₹${value.toLocaleString()}`,
      //     fontWeight: 600,
      //   },
      //   itemStyle: { color: primaryColor },
      // },
    })),
  };

  return (
    <div className="revenue-card">
      <div className="card-title-row">
        <div className="card-title">Total Revenue</div>

        <select
          value={range}
          onChange={(e) => onRangeChange(e.target.value)}
          className="custom-select"
        >
          <option value="this_month">This Month</option>
          <option value="1w">1W</option>
          <option value="1m">1M</option>
          <option value="3m">3M</option>
          <option value="6m">6M</option>
          <option value="1y">1Y</option>
        </select>
      </div>

      <ReactECharts
        option={option}
        style={{ height: 300, width: "100%" }}
        notMerge
        lazyUpdate
      />
    </div>
  );
}
